import React, { ChangeEvent, useContext } from "react";
import { Select, DatePicker, Tooltip, Input } from "antd";

import { rangePresets } from "constants/constants";
import dayjs from "dayjs";

import { filterLabels, filtersConfig } from "utils/filterUtils";
import MultiGenderSelect from "common/MultiGenderSelect";
import MultiTeamRoleSelect from "common/MultiTeamRoleSelect";
import MultiDesignationSelect from "common/MultiDesignationSelect";
import MultiShiftSelect from "common/MultiShiftSelect";
import { getCustomFieldKey } from "utils/commonUtils";
import { useSelector } from "react-redux";

interface Props {
  filter: Array<any>;
  setFilter: (filter: Array<any>) => void;
  firstFilter: boolean;
}

const FilterUnit = ({ filter, setFilter, firstFilter }: Props) => {
  const {
    workplace_setting_data,
  }: {
    workplace_setting_data: AdminSetting;
    // @ts-ignore
  } = useSelector((state) => state.workplaceSettings);

  const customFieldsMap: {
    [key: string]: CustomField;
  } =
    workplace_setting_data?.workplace?.custom_fields?.reduce((acc, field) => {
      return { ...acc, [getCustomFieldKey(field)]: field };
    }, {}) || {};

  const onFilterChange = (
    index: number,
    value: any,
    filter,
    nextFilterConfigUnit?: FilterConfigUnit
  ) => {
    if (index > 3) {
      setFilter(filter);
      return;
    }

    const newFilter = [...filter];
    newFilter[index] = value;

    if (nextFilterConfigUnit && nextFilterConfigUnit.type === "OPTION") {
      const nextValue = Object.keys(nextFilterConfigUnit.options)[0];
      onFilterChange(
        index + 1,
        nextValue,
        newFilter,
        nextFilterConfigUnit.options[nextValue]
      );
    } else {
      for (let i = index + 1; i < 4; i++) {
        newFilter[i] = null;
      }

      setFilter(newFilter);
    }
  };

  const renderFilterElement = (
    filterConfigUnit: FilterConfigUnit,
    index: number
  ): React.ReactNode => {
    switch (filterConfigUnit.type) {
      case "VALUE":
        return null;

      case "OPTION":
        return (
          <Select
            dropdownMatchSelectWidth={false}
            className={`col-span-${
              filter[index] &&
              filterConfigUnit.options[filter[index]].type === "VALUE"
                ? 8 - 2 * (index - 1)
                : 2
            }`}
            value={filter[index]}
            placeholder="Select filter"
            autoFocus
            onChange={(value) =>
              onFilterChange(
                index,
                value,
                filter,
                filterConfigUnit.options[value]
              )
            }
          >
            {Object.keys(filterConfigUnit.options).map((property) => (
              <Select.Option key={property} value={property}>
                {property.split("_").join(" ")}
              </Select.Option>
            ))}
          </Select>
        );

      case "MULTI_SELECT_GENDER_TYPE":
        return (
          <MultiGenderSelect
            className={`col-span-${8 - 2 * (index - 1)}`}
            value={filter[index] || []}
            onChange={(value) => onFilterChange(index, value, filter)}
          />
        );

      case "MULTI_SELECT_TEAM_ROLE_TYPE":
        return (
          <MultiTeamRoleSelect
            className={`col-span-${8 - 2 * (index - 1)}`}
            value={filter[index] || []}
            onChange={(value) => onFilterChange(index, value, filter)}
          />
        );

      case "MULTI_SELECT_DESIGNATION_TYPE":
        return (
          <MultiDesignationSelect
            className={`col-span-${8 - 2 * (index - 1)}`}
            value={filter[index] || []}
            onChange={(value) => onFilterChange(index, value, filter)}
          />
        );

      case "MULTI_SELECT_SHIFT_TYPE":
        return (
          <MultiShiftSelect
            className={`col-span-${8 - 2 * (index - 1)}`}
            value={filter[index] || []}
            onChange={(value) => onFilterChange(index, value, filter)}
          />
        );

      case "DATE_RANGE_PICKER":
        return (
          <DatePicker.RangePicker
            inputReadOnly
            // @ts-ignore
            presets={rangePresets}
            value={
              filter[index] && [
                dayjs(filter[index][0]),
                dayjs(filter[index][1]),
              ]
            }
            onChange={(v, dateStrings) => {
              if (
                filter[0] === "date_of_joining" ||
                filter[0] === "date_of_birth"
              ) {
                onFilterChange(index, dateStrings, filter);
                return;
              }
              const values = [
                dateStrings[0] && new Date(dateStrings[0]).setHours(0, 0, 0),
                dateStrings[1] && new Date(dateStrings[1]).setHours(23, 59, 59),
              ];
              onFilterChange(index, values, filter);
            }}
            className={`col-span-${8 - 2 * (index - 1)}`}
            clearIcon={null}
          />
        );

      case "DATE_PICKER":
        return (
          <DatePicker
            inputReadOnly
            // @ts-ignore
            presets={rangePresets}
            value={filter[index] && dayjs(filter[index])}
            onChange={(v, dateString) => {
              if (
                filter[0] === "date_of_joining" ||
                filter[0] === "date_of_birth"
              ) {
                onFilterChange(index, dateString, filter);
                return;
              }
              const value =
                dateString && new Date(dateString).setHours(0, 0, 0);

              onFilterChange(index, value, filter);
            }}
            className={`col-span-${8 - 2 * (index - 1)}`}
            clearIcon={null}
          />
        );

      case "INTEGER_INPUT":
        return (
          <Input
            className={`col-span-${8 - 2 * (index - 1)}`}
            type="number"
            min={-2147483648}
            max={2147483647}
            value={filter[index]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (/^-?\d+$/.test(value)) {
                onFilterChange(index, value, filter);
              }
            }}
          />
        );

      case "STRING_INPUT":
        return (
          <Input
            className={`col-span-${8 - 2 * (index - 1)}`}
            value={filter[index]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;

              onFilterChange(index, value, filter);
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-12 w-full items-center gap-2">
      <div className="col-span-1"> {firstFilter ? "Where" : "AND"}</div>
      <Select
        dropdownMatchSelectWidth={false}
        className="col-span-3"
        value={filter[0]}
        placeholder="Select filter"
        onChange={(value) =>
          onFilterChange(0, value, filter, filtersConfig[value])
        }
        defaultOpen
      >
        {Object.keys(filtersConfig)
          .filter(
            (property) =>
              (!property.startsWith("cint") &&
                !property.startsWith("cstring") &&
                !property.startsWith("cbool")) ||
              Object.keys(customFieldsMap).includes(property)
          )
          .map((property) => (
            <Select.Option
              key={property}
              value={property}
              disabled={!!filtersConfig[property].disabled}
            >
              {filtersConfig[property].disabled ? (
                <Tooltip title="Coming soon!" placement="bottom">
                  {filterLabels[property] || customFieldsMap[property]?.label}
                </Tooltip>
              ) : (
                filterLabels[property] || customFieldsMap[property]?.label
              )}
            </Select.Option>
          ))}
      </Select>
      {filter
        .filter(
          (filterValue) =>
            !!filterValue &&
            (!Array.isArray(filterValue) || filterValue.length > 0)
        )
        .map((filterValue, index) => {
          let filterConfigUnit = filtersConfig[filter[0]];
          for (let i = 1; i <= index; i++) {
            if (filterConfigUnit.type === "OPTION") {
              filterConfigUnit = filterConfigUnit.options?.[filter[i]];
            } else {
              return null;
            }
          }

          return renderFilterElement(filterConfigUnit, index + 1);
        })}
    </div>
  );
};

export default FilterUnit;
